import { useCallback, useState } from 'react';
import {
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDaysInMonth,
  isToday,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import {
  classifyBookingsWithHistory,
  type BookingCustomerRow,
} from '../lib/customerNewRepeatBookings';
import {
  fetchStandaloneInvoiceSales,
  sumStandaloneInvoiceSales,
} from '../lib/invoices/standaloneInvoiceSales';
import { averageSale } from '../lib/salesUnits';

export interface SalesDayRow {
  date: string;
  label: string;
  bookings: number;
  invoices: number;
  total: number;
  isToday: boolean;
}

export interface SalesShareRow {
  key: string;
  label: string;
  amount: number;
  count: number;
  /** 0..1 share of the section total. */
  share: number;
}

export interface SalesFeedRow {
  id: string;
  name: string;
  detail: string;
  amount: number;
  isNew: boolean | null;
}

export interface SalesFeedDay {
  date: string;
  label: string;
  isToday: boolean;
  rows: SalesFeedRow[];
}

export interface SalesMonthSummary {
  /** Booking sales + counter-sale invoices — matches the dashboard tile. */
  totalSales: number;
  bookingSales: number;
  invoiceSales: number;
  bookingCount: number;
  invoiceCount: number;
  grossSubtotal: number;
  discounts: number;
  avgSale: number;
  salesToday: number;
  avgPerDay: number;
  projectedMonthEnd: number;
  /** All of last month, for a straight month-over-month comparison. */
  lastMonthTotal: number;
  newCount: number;
  repeatCount: number;
  newRevenue: number;
  repeatRevenue: number;
  /** Sales by when they were BOOKED (created), not delivered. */
  bookedThisMonth: number;
  bookedToday: number;
}

const EMPTY_SUMMARY: SalesMonthSummary = {
  totalSales: 0,
  bookingSales: 0,
  invoiceSales: 0,
  bookingCount: 0,
  invoiceCount: 0,
  grossSubtotal: 0,
  discounts: 0,
  avgSale: 0,
  salesToday: 0,
  avgPerDay: 0,
  projectedMonthEnd: 0,
  lastMonthTotal: 0,
  newCount: 0,
  repeatCount: 0,
  newRevenue: 0,
  repeatRevenue: 0,
  bookedThisMonth: 0,
  bookedToday: 0,
};

type MonthBookingRow = BookingCustomerRow & {
  subtotal: number | null;
  discount_amount: number | null;
  source: string | null;
  booking_number: number | null;
  customer: { first_name: string | null; last_name: string | null } | null;
  booking_services: { name: string | null; total_amount: number | null }[] | null;
};

function bookingNet(b: { subtotal: number | null; discount_amount: number | null }): number {
  return (b.subtotal ?? 0) - (b.discount_amount ?? 0);
}

/** end_date can be 'yyyy-MM-dd' or a timestamp — normalize to the local day key. */
function dayKeyOf(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return format(new Date(value), 'yyyy-MM-dd');
}

/**
 * Everything the Sales this month modal shows. Same sale definition as the
 * dashboard tile (useCalendarSalesMetrics): non-cancelled bookings by end_date
 * at subtotal − discount, plus issued standalone invoices by finalized_at.
 */
export function useSalesMonthDetails() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [summary, setSummary] = useState<SalesMonthSummary>(EMPTY_SUMMARY);
  const [days, setDays] = useState<SalesDayRow[]>([]);
  const [bookedDays, setBookedDays] = useState<SalesDayRow[]>([]);
  const [sources, setSources] = useState<SalesShareRow[]>([]);
  const [topServices, setTopServices] = useState<SalesShareRow[]>([]);
  const [feedDays, setFeedDays] = useState<SalesFeedDay[]>([]);
  const [monthLabel, setMonthLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setSummary(EMPTY_SUMMARY);
      setDays([]);
      setBookedDays([]);
      setSources([]);
      setTopServices([]);
      setFeedDays([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      const lastMonth = subMonths(now, 1);
      setMonthLabel(format(now, 'MMMM yyyy'));

      const bookingFilters = (query: ReturnType<typeof supabase.from>) =>
        query
          .eq('business_id', businessId)
          .eq('is_deleted', false)
          .neq('status', 'cancelled')
          .or('in_que.is.null,in_que.eq.false');

      const [monthResult, lastMonthResult, createdResult, monthInvoices, lastMonthInvoices] = await Promise.all([
        bookingFilters(
          supabase
            .from('bookings')
            .select(
              'id, subtotal, discount_amount, customer_id, customer_repeat_status, end_date, created_at, source, booking_number, customer:customers!bookings_customer_id_fkey(first_name, last_name), booking_services(name, total_amount)',
            ),
        )
          .gte('end_date', format(monthStart, 'yyyy-MM-dd'))
          .lte('end_date', format(monthEnd, 'yyyy-MM-dd')),
        bookingFilters(
          supabase.from('bookings').select('id, subtotal, discount_amount, end_date'),
        )
          .gte('end_date', format(startOfMonth(lastMonth), 'yyyy-MM-dd'))
          .lte('end_date', format(endOfMonth(lastMonth), 'yyyy-MM-dd')),
        // Bookings CREATED this month (booked axis), whatever their job dates.
        bookingFilters(
          supabase.from('bookings').select('id, subtotal, discount_amount, created_at'),
        )
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', now.toISOString()),
        fetchStandaloneInvoiceSales(businessId, { start: monthStart, end: monthEnd }),
        fetchStandaloneInvoiceSales(businessId, {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth),
        }),
      ]);

      if (monthResult.error) throw monthResult.error;
      if (createdResult.error) throw createdResult.error;
      const monthRows = (monthResult.data ?? []) as unknown as MonthBookingRow[];

      // New vs repeat — same classifier as the dashboard ring.
      const classified = await classifyBookingsWithHistory(
        monthRows as BookingCustomerRow[],
        supabase,
        bookingFilters,
      );
      const isNewById = new Map(classified.map((row) => [row.id, row.isNew]));

      let grossSubtotal = 0;
      let discounts = 0;
      let newCount = 0;
      let repeatCount = 0;
      let newRevenue = 0;
      let repeatRevenue = 0;
      const byDay = new Map<string, { bookings: number; invoices: number }>();
      const bySource = new Map<string, { amount: number; count: number }>();
      const byService = new Map<string, { amount: number; count: number }>();

      for (const row of monthRows) {
        const net = bookingNet(row);
        grossSubtotal += row.subtotal ?? 0;
        discounts += row.discount_amount ?? 0;

        const isNew = isNewById.get(row.id) ?? true;
        if (isNew) {
          newCount += 1;
          newRevenue += net;
        } else {
          repeatCount += 1;
          repeatRevenue += net;
        }

        const dayKey = dayKeyOf(row.end_date);
        const day = byDay.get(dayKey) ?? { bookings: 0, invoices: 0 };
        day.bookings += net;
        byDay.set(dayKey, day);

        const sourceKey = (row.source ?? '').trim() || 'Unknown';
        const source = bySource.get(sourceKey) ?? { amount: 0, count: 0 };
        source.amount += net;
        source.count += 1;
        bySource.set(sourceKey, source);

        for (const svc of row.booking_services ?? []) {
          const name = svc.name?.trim() || 'Unnamed service';
          const service = byService.get(name) ?? { amount: 0, count: 0 };
          service.amount += svc.total_amount ?? 0;
          service.count += 1;
          byService.set(name, service);
        }
      }

      const bookingSales = grossSubtotal - discounts;
      const invoiceSales = sumStandaloneInvoiceSales(monthInvoices);
      const totalSales = bookingSales + invoiceSales;

      for (const inv of monthInvoices) {
        const dayKey = format(new Date(inv.finalizedAt), 'yyyy-MM-dd');
        const day = byDay.get(dayKey) ?? { bookings: 0, invoices: 0 };
        day.invoices += inv.amount;
        byDay.set(dayKey, day);
      }

      // Straight month-over-month: this month's total vs all of last month.
      // Both sides count the full calendar month on the same delivery basis,
      // which is also what the dashboard tile compares.
      const elapsedDays = differenceInCalendarDays(now, monthStart) + 1;
      let lastMonthTotal = 0;
      for (const row of (lastMonthResult.data ?? []) as {
        subtotal: number | null;
        discount_amount: number | null;
        end_date: string;
      }[]) {
        lastMonthTotal += bookingNet(row);
      }
      for (const inv of lastMonthInvoices) {
        lastMonthTotal += inv.amount;
      }

      // Chart spans the full month: future-ending scheduled jobs count as sales
      // this month (same as the tile), so they belong on the chart.
      const todayKey = format(now, 'yyyy-MM-dd');
      const dayRows: SalesDayRow[] = eachDayOfInterval({
        start: startOfDay(monthStart),
        end: startOfDay(monthEnd),
      }).map((day) => {
        const key = format(day, 'yyyy-MM-dd');
        const sums = byDay.get(key) ?? { bookings: 0, invoices: 0 };
        return {
          date: key,
          label: format(day, 'MMM d'),
          bookings: sums.bookings,
          invoices: sums.invoices,
          total: sums.bookings + sums.invoices,
          isToday: key === todayKey,
        };
      });

      // Month-to-date pace (future-scheduled sales excluded from the average).
      let mtdSales = 0;
      let salesToday = 0;
      for (const day of dayRows) {
        if (day.date <= todayKey) mtdSales += day.total;
        if (day.date === todayKey) salesToday = day.total;
      }
      const avgPerDay = elapsedDays > 0 ? mtdSales / elapsedDays : 0;
      const projectedMonthEnd = Math.max(totalSales, avgPerDay * getDaysInMonth(now));

      // Booked axis: bookings created this month + invoices issued this month
      // (a counter sale is booked and delivered the same moment).
      let bookedBookings = 0;
      const bookedByDay = new Map<string, { bookings: number; invoices: number }>();
      for (const row of (createdResult.data ?? []) as {
        subtotal: number | null;
        discount_amount: number | null;
        created_at: string;
      }[]) {
        const net = bookingNet(row);
        bookedBookings += net;
        const dayKey = format(new Date(row.created_at), 'yyyy-MM-dd');
        const day = bookedByDay.get(dayKey) ?? { bookings: 0, invoices: 0 };
        day.bookings += net;
        bookedByDay.set(dayKey, day);
      }
      for (const inv of monthInvoices) {
        const dayKey = format(new Date(inv.finalizedAt), 'yyyy-MM-dd');
        const day = bookedByDay.get(dayKey) ?? { bookings: 0, invoices: 0 };
        day.invoices += inv.amount;
        bookedByDay.set(dayKey, day);
      }
      // Nothing can be booked in the future — the booked series ends today.
      const bookedDayRows: SalesDayRow[] = eachDayOfInterval({
        start: startOfDay(monthStart),
        end: startOfDay(now),
      }).map((day) => {
        const key = format(day, 'yyyy-MM-dd');
        const sums = bookedByDay.get(key) ?? { bookings: 0, invoices: 0 };
        return {
          date: key,
          label: format(day, 'MMM d'),
          bookings: sums.bookings,
          invoices: sums.invoices,
          total: sums.bookings + sums.invoices,
          isToday: key === todayKey,
        };
      });
      const bookedThisMonth = bookedBookings + invoiceSales;
      const bookedTodaySums = bookedByDay.get(todayKey) ?? { bookings: 0, invoices: 0 };
      const bookedToday = bookedTodaySums.bookings + bookedTodaySums.invoices;

      const sourceRows: SalesShareRow[] = Array.from(bySource.entries()).map(([key, value]) => ({
        key,
        label: key === 'Unknown' ? 'No source recorded' : key,
        amount: value.amount,
        count: value.count,
        share: 0,
      }));
      if (invoiceSales > 0 || monthInvoices.length > 0) {
        sourceRows.push({
          key: '__invoices',
          label: 'Counter sales & invoices',
          amount: invoiceSales,
          count: monthInvoices.length,
          share: 0,
        });
      }
      for (const row of sourceRows) row.share = totalSales > 0 ? row.amount / totalSales : 0;
      sourceRows.sort((a, b) => b.amount - a.amount);

      const serviceRevenueTotal = Array.from(byService.values()).reduce(
        (sum, s) => sum + s.amount,
        0,
      );
      const serviceRows: SalesShareRow[] = Array.from(byService.entries())
        .map(([name, value]) => ({
          key: name,
          label: name,
          amount: value.amount,
          count: value.count,
          share: serviceRevenueTotal > 0 ? value.amount / serviceRevenueTotal : 0,
        }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 6);

      // Feed: bookings + counter sales, newest day first (future jobs on top).
      type FeedEntry = { dayKey: string; row: SalesFeedRow };
      const feedEntries: FeedEntry[] = [];
      for (const row of monthRows) {
        const customerName =
          `${row.customer?.first_name ?? ''} ${row.customer?.last_name ?? ''}`.trim() ||
          'No customer';
        feedEntries.push({
          dayKey: dayKeyOf(row.end_date),
          row: {
            id: row.id,
            name: customerName,
            detail: row.booking_number != null ? `Booking #${row.booking_number}` : 'Booking',
            amount: bookingNet(row),
            isNew: isNewById.get(row.id) ?? null,
          },
        });
      }
      for (const inv of monthInvoices) {
        feedEntries.push({
          dayKey: format(new Date(inv.finalizedAt), 'yyyy-MM-dd'),
          row: {
            id: inv.id,
            name: 'Counter sale',
            detail: 'Invoice',
            amount: inv.amount,
            isNew: null,
          },
        });
      }
      feedEntries.sort(
        (a, b) => b.dayKey.localeCompare(a.dayKey) || b.row.amount - a.row.amount,
      );
      const feedByDay = new Map<string, SalesFeedDay>();
      for (const entry of feedEntries) {
        let day = feedByDay.get(entry.dayKey);
        if (!day) {
          const date = new Date(`${entry.dayKey}T00:00:00`);
          day = {
            date: entry.dayKey,
            label: format(date, 'EEE, MMM d'),
            isToday: isToday(date),
            rows: [],
          };
          feedByDay.set(entry.dayKey, day);
        }
        day.rows.push(entry.row);
      }

      setSummary({
        totalSales,
        bookingSales,
        invoiceSales,
        bookingCount: monthRows.length,
        invoiceCount: monthInvoices.length,
        grossSubtotal,
        discounts,
        avgSale: averageSale(totalSales, monthRows.length, monthInvoices.length),
        salesToday,
        avgPerDay,
        projectedMonthEnd,
        lastMonthTotal,
        newCount,
        repeatCount,
        newRevenue,
        repeatRevenue,
        bookedThisMonth,
        bookedToday,
      });
      setDays(dayRows);
      setBookedDays(bookedDayRows);
      setSources(sourceRows);
      setTopServices(serviceRows);
      setFeedDays(Array.from(feedByDay.values()));
    } catch (err) {
      console.error('Error loading sales month details:', err);
      setSummary(EMPTY_SUMMARY);
      setDays([]);
      setBookedDays([]);
      setSources([]);
      setTopServices([]);
      setFeedDays([]);
      setError('Could not load sales details.');
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId]);

  return {
    summary,
    days,
    bookedDays,
    sources,
    topServices,
    feedDays,
    monthLabel,
    loading,
    error,
    load,
  };
}
